import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllProducts, getAllCategories, createProduct, updateProduct, deleteProduct } from '../action';
import { categoryService } from '../services/categoryService';
import { mercadoPagoService } from '../services/mercadoPagoService';

function AdminPanel() {
  const dispatch = useDispatch();
  const { products, categories, user } = useSelector(state => state);

  const [showProductForm, setShowProductForm] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [productForm, setProductForm] = useState({
    title: '',
    description: '',
    price: '',
    currency: 'ARS',
    stock: '',
    categoryId: '',
    image: null,
    images: [],
    existingImageUrls: []
  });
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState([]);
  const [categoryFormErrors, setCategoryFormErrors] = useState([]);
  const [activeTab, setActiveTab] = useState('products'); // 'products' | 'categories' | 'mercadopago'
  const [isSubmittingProduct, setIsSubmittingProduct] = useState(false);
  const [isSubmittingCategory, setIsSubmittingCategory] = useState(false);
  const [mercadoPagoConfig, setMercadoPagoConfig] = useState({
    accessToken: '',
    publicKey: ''
  });
  const [mercadoPagoErrors, setMercadoPagoErrors] = useState([]);
  const [mercadoPagoSuccess, setMercadoPagoSuccess] = useState('');
  const [isLoadingMercadoPago, setIsLoadingMercadoPago] = useState(false);
  const [isSavingMercadoPago, setIsSavingMercadoPago] = useState(false);

  useEffect(() => {
    dispatch(getAllProducts());
    dispatch(getAllCategories());
  }, [dispatch]);

  useEffect(() => {
    const loadMercadoPagoConfig = async () => {
      if (user?.role !== 'admin') {
        return;
      }

      setIsLoadingMercadoPago(true);
      setMercadoPagoErrors([]);
      try {
        const data = await mercadoPagoService.getConfig();
        setMercadoPagoConfig({
          accessToken: data?.accessToken || '',
          publicKey: data?.publicKey || ''
        });
      } catch (error) {
        const message = error.response?.data?.message || 'No se pudo cargar la configuracion de Mercado Pago';
        setMercadoPagoErrors(Array.isArray(message) ? message : [message]);
      } finally {
        setIsLoadingMercadoPago(false);
      }
    };

    loadMercadoPagoConfig();
  }, [user]);

  const handleMercadoPagoChange = (field, value) => {
    setMercadoPagoConfig((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveMercadoPago = async (e) => {
    e.preventDefault();
    setMercadoPagoErrors([]);
    setMercadoPagoSuccess('');
    setIsSavingMercadoPago(true);

    try {
      await mercadoPagoService.saveConfig({
        accessToken: mercadoPagoConfig.accessToken,
        publicKey: mercadoPagoConfig.publicKey
      });
      setMercadoPagoSuccess('Credenciales guardadas correctamente.');
    } catch (error) {
      const message = error.response?.data?.message || 'No se pudo guardar la configuracion de Mercado Pago';
      setMercadoPagoErrors(Array.isArray(message) ? message : [message]);
    } finally {
      setIsSavingMercadoPago(false);
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    setFormErrors([]);
    setIsSubmittingProduct(true);

    let formData = {
      ...productForm,
      price: parseFloat(productForm.price),
      stock: parseInt(productForm.stock, 10)
    };

    // Si estamos editando, rellena los campos vacíos con los valores originales
    if (editingProduct) {
      formData = {
        title: formData.title || editingProduct.title,
        description: formData.description || editingProduct.description,
        price: formData.price || editingProduct.price,
        currency: formData.currency || editingProduct.currency || 'ARS',
        stock: formData.stock !== undefined ? formData.stock : editingProduct.stock,
        categoryId: formData.categoryId || (editingProduct.category?.id || ''),
        image: formData.image, // la imagen solo si se selecciona una nueva
        images: formData.images || [],
        imageUrls: formData.existingImageUrls || [] // Las imágenes existentes que queremos mantener
      };
    }
    console.log('Form Data:', formData);
    let result;
    if (editingProduct) {
      result = await dispatch(updateProduct(editingProduct.id, formData));
    } else {
      result = await dispatch(createProduct(formData));
    }
    // Manejo de errores del backend
    if (result.success) {
      setShowProductForm(false);
      setEditingProduct(null);
      setProductForm({
        title: '',
        description: '',
        price: '',
        currency: 'ARS',
        stock: '',
        categoryId: '',
        image: null,
        images: [],
        existingImageUrls: []
      });
      dispatch(getAllProducts());
    } else if (result.error) {
      // Si el backend devuelve un array de errores
      if (Array.isArray(result.error)) {
        setFormErrors(result.error);
      } else if (typeof result.error === 'string') {
        setFormErrors([result.error]);
      } else if (result.error?.message) {
        setFormErrors([result.error.message]);
      }
    }
    setIsSubmittingProduct(false);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setCategoryFormErrors([]);
    setIsSubmittingCategory(true);

    try {
      if (editingCategory) {
        await categoryService.updateCategory(editingCategory.id, categoryForm);
      } else {
        await categoryService.createCategory(categoryForm);
      }
      setShowCategoryForm(false);
      setEditingCategory(null);
      setCategoryForm({ name: '', description: '' });
      dispatch(getAllCategories());
    } catch (error) {
      console.error('Error with category:', error);
      if (error.response?.data?.message) {
        if (Array.isArray(error.response.data.message)) {
          setCategoryFormErrors(error.response.data.message);
        } else {
          setCategoryFormErrors([error.response.data.message]);
        }
      } else {
        setCategoryFormErrors(['Error al procesar la categoría']);
      }
    } finally {
      setIsSubmittingCategory(false);
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      title: product.title,
      description: product.description,
      price: product.price.toString(),
      currency: product.currency || 'ARS',
      stock: product.stock.toString(),
      categoryId: product.category?.id || '',
      image: null,
      images: [],
      existingImageUrls: product.imageUrls || []
    });
    setShowProductForm(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este producto?')) {
      const result = await dispatch(deleteProduct(productId));
      if (result.success) {
        dispatch(getAllProducts());
      }
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || ''
    });
    setShowCategoryForm(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta categoría? Esto podría afectar a los productos asociados.')) {
      try {
        await categoryService.deleteCategory(categoryId);
        dispatch(getAllCategories());
      } catch (error) {
        console.error('Error deleting category:', error);
        alert('Error al eliminar la categoría. Puede que tenga productos asociados.');
      }
    }
  };

  const handleImageChange = (e) => {
    setProductForm({
      ...productForm,
      image: e.target.files[0]
    });
  };

  const handleMultipleImagesChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setProductForm({
      ...productForm,
      images: [...productForm.images, ...newFiles]
    });
    // Limpiar el input para permitir seleccionar los mismos archivos de nuevo si es necesario
    e.target.value = '';
  };

  const removeNewImage = (indexToRemove) => {
    const updatedImages = productForm.images.filter((_, index) => index !== indexToRemove);
    setProductForm({
      ...productForm,
      images: updatedImages
    });
  };

  const removeExistingImage = (indexToRemove) => {
    const updatedUrls = productForm.existingImageUrls.filter((_, index) => index !== indexToRemove);
    setProductForm({
      ...productForm,
      existingImageUrls: updatedUrls
    });
  };

  const removeAllExistingImages = () => {
    if (productForm.existingImageUrls.length > 1) {
      if (window.confirm(`¿Estás seguro de que quieres eliminar todas las ${productForm.existingImageUrls.length} imágenes actuales? Esta acción no se puede deshacer.`)) {
        setProductForm({ ...productForm, existingImageUrls: [] });
      }
    } else {
      setProductForm({ ...productForm, existingImageUrls: [] });
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Acceso Denegado</h2>
          <p className="text-gray-600">No tienes permisos para acceder al panel de administración.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mt-14 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Panel de Administración</h1>
          <p className="mt-2 text-gray-600">Gestiona productos y categorías</p>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('products')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'products'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Productos ({products.length})
              </button>
              <button
                onClick={() => setActiveTab('categories')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'categories'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Categorías ({categories.length})
              </button>
              <button
                onClick={() => setActiveTab('mercadopago')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'mercadopago'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Mercado Pago (produccion)
              </button>
            </nav>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="mb-8 flex space-x-4">
          {activeTab === 'products' && (
            <button
              onClick={() => setShowProductForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              Agregar Producto
            </button>
          )}
          {activeTab === 'categories' && (
            <button
              onClick={() => setShowCategoryForm(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
            >
              Agregar Categoría
            </button>
          )}
        </div>

        {/* Formulario de Producto */}
        {showProductForm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                {editingProduct ? 'Editar Producto' : 'Agregar Producto'}
              </h3>
              {/* Mostrar errores */}
              {formErrors.length > 0 && (
                <div className="mb-4">
                  {formErrors.map((err, idx) => (
                    <div key={idx} className="text-red-600 text-sm">{err}</div>
                  ))}
                </div>
              )}
              <form onSubmit={handleProductSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Título</label>
                  <input
                    type="text"
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    value={productForm.title}
                    onChange={(e) => setProductForm({ ...productForm, title: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Descripción</label>
                  <textarea
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    value={productForm.description}
                    onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Precio</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    value={productForm.price}
                    onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Moneda</label>
                  <select
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    value={productForm.currency}
                    onChange={(e) => setProductForm({ ...productForm, currency: e.target.value })}
                  >
                    <option value="ARS">Pesos (ARS)</option>
                    <option value="USD">Dólares (USD)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Stock</label>
                  <input
                    type="number"
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    value={productForm.stock}
                    onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Categoría</label>
                  <select
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    value={productForm.categoryId}
                    onChange={(e) => setProductForm({ ...productForm, categoryId: e.target.value })}
                  >
                    <option value="">Seleccionar categoría</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Imagen Principal (Opcional)</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="mt-1 block w-full"
                    onChange={handleImageChange}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Galería de Imágenes</label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="mt-1 block w-full"
                    onChange={handleMultipleImagesChange}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Puedes seleccionar múltiples imágenes a la vez o agregar de una en una. 
                    Las nuevas imágenes se agregarán a la galería existente.
                  </p>
                </div>

                {/* Mostrar imágenes existentes si estamos editando */}
                {editingProduct && productForm.existingImageUrls.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Imágenes Actuales ({productForm.existingImageUrls.length})
                      </label>
                      <button
                        type="button"
                        onClick={removeAllExistingImages}
                        className="text-xs text-red-600 hover:text-red-800 underline"
                      >
                        Eliminar todas las actuales
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {productForm.existingImageUrls.map((imageUrl, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={imageUrl.startsWith('http') ? imageUrl : `http://localhost:4200${imageUrl}`}
                            alt={`Producto ${index + 1}`}
                            className="w-16 h-16 object-cover rounded border transition-opacity group-hover:opacity-75"
                          />
                          <button
                            type="button"
                            onClick={() => removeExistingImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                            title="Eliminar esta imagen"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-amber-600 mt-1">
                      ⚠️ Las imágenes marcadas para eliminar se quitarán permanentemente al guardar.
                    </p>
                  </div>
                )}

                {/* Preview de nuevas imágenes */}
                {productForm.images.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Nuevas Imágenes a Subir ({productForm.images.length})
                      </label>
                      <button
                        type="button"
                        onClick={() => setProductForm({ ...productForm, images: [] })}
                        className="text-xs text-red-600 hover:text-red-800 underline"
                      >
                        Limpiar todas
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {Array.from(productForm.images).map((file, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Nueva ${index + 1}`}
                            className="w-16 h-16 object-cover rounded border border-green-300 transition-opacity group-hover:opacity-75"
                          />
                          <button
                            type="button"
                            onClick={() => removeNewImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                            title="Eliminar esta imagen nueva"
                          >
                            ×
                          </button>
                          {/* Indicador de imagen nueva */}
                          <div className="absolute bottom-0 left-0 bg-green-500 text-white text-xs px-1 rounded-tr">
                            NEW
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-green-600 mt-1">
                      ✅ Estas imágenes se subirán al guardar el producto.
                    </p>
                  </div>
                )}

                {/* Resumen de cambios en imágenes - Editando producto */}
                {editingProduct && (productForm.existingImageUrls.length > 0 || productForm.images.length > 0) && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">📋 Resumen de imágenes:</h4>
                    <div className="text-xs text-blue-700 space-y-1">
                      <div>• <strong>Imágenes actuales que se mantendrán:</strong> {productForm.existingImageUrls.length}</div>
                      <div>• <strong>Imágenes nuevas que se agregarán:</strong> {productForm.images.length}</div>
                      <div>• <strong>Total final de imágenes:</strong> {productForm.existingImageUrls.length + productForm.images.length}</div>
                      {editingProduct.imageUrls && editingProduct.imageUrls.length !== productForm.existingImageUrls.length && (
                        <div className="text-amber-700">• <strong>Imágenes que se eliminarán:</strong> {editingProduct.imageUrls.length - productForm.existingImageUrls.length}</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Resumen para producto nuevo */}
                {!editingProduct && (productForm.images.length > 0 || productForm.image) && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-green-800 mb-2">📋 Imágenes del nuevo producto:</h4>
                    <div className="text-xs text-green-700 space-y-1">
                      {productForm.image && <div>• <strong>Imagen principal:</strong> 1</div>}
                      <div>• <strong>Galería de imágenes:</strong> {productForm.images.length}</div>
                      <div>• <strong>Total de imágenes:</strong> {(productForm.image ? 1 : 0) + productForm.images.length}</div>
                    </div>
                  </div>
                )}

                <div className="flex space-x-2">
                  <button
                    type="submit"
                    disabled={isSubmittingProduct}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmittingProduct ? 'Guardando...' : (editingProduct ? 'Actualizar' : 'Crear')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowProductForm(false);
                      setEditingProduct(null);
                      setFormErrors([]);
                      setProductForm({
                        title: '',
                        description: '',
                        price: '',
                        currency: 'ARS',
                        stock: '',
                        categoryId: '',
                        image: null,
                        images: [],
                        existingImageUrls: []
                      });
                      // Limpiar URLs de objetos creados para preview
                      productForm.images.forEach(file => {
                        if (file instanceof File) {
                          URL.revokeObjectURL(URL.createObjectURL(file));
                        }
                      });
                    }}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Formulario de Categoría */}
        {showCategoryForm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                {editingCategory ? 'Editar Categoría' : 'Agregar Categoría'}
              </h3>
              {/* Mostrar errores de categoría */}
              {categoryFormErrors.length > 0 && (
                <div className="mb-4">
                  {categoryFormErrors.map((err, idx) => (
                    <div key={idx} className="text-red-600 text-sm">{err}</div>
                  ))}
                </div>
              )}
              <form onSubmit={handleCategorySubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nombre</label>
                  <input
                    type="text"
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Descripción</label>
                  <textarea
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    value={categoryForm.description}
                    onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    type="submit"
                    disabled={isSubmittingCategory}
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmittingCategory ? 'Guardando...' : (editingCategory ? 'Actualizar' : 'Crear')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCategoryForm(false);
                      setEditingCategory(null);
                      setCategoryForm({ name: '', description: '' });
                      setCategoryFormErrors([]);
                    }}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Contenido de los Tabs */}
        {activeTab === 'products' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Lista de Productos
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                Gestiona todos los productos de tu tienda
              </p>
            </div>
            {products.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <p className="text-gray-500">No hay productos registrados</p>
                <button
                  onClick={() => setShowProductForm(true)}
                  className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Crear primer producto
                </button>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {products.map((product) => (
                  <li key={product.id} className="px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex mr-4">
                        {/* Mostrar primera imagen de imageUrls o imageUrl como fallback */}
                        {(product.imageUrls && product.imageUrls.length > 0) ? (
                          <div className="relative">
                            <img
                              src={
                                product.imageUrls[0].startsWith('http')
                                  ? product.imageUrls[0]
                                  : `http://localhost:4200${product.imageUrls[0]}`
                              }
                              alt={product.title}
                              className="h-16 w-16 object-cover rounded-md"
                            />
                            {/* Indicador de múltiples imágenes */}
                            {product.imageUrls.length > 1 && (
                              <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                {product.imageUrls.length}
                              </div>
                            )}
                          </div>
                        ) : product.imageUrl ? (
                          <img
                            src={
                              product.imageUrl.startsWith('http')
                                ? product.imageUrl
                                : `http://localhost:4200${product.imageUrl}`
                            }
                            alt={product.title}
                            className="h-16 w-16 object-cover rounded-md"
                          />
                        ) : (
                          <div className="h-16 w-16 bg-gray-200 rounded-md flex items-center justify-center">
                            <span className="text-gray-400 text-xs">Sin imagen</span>
                          </div>
                        )}
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">{product.title}</h4>
                        <p className="text-sm text-gray-500">{product.category?.name}</p>
                        <p className="text-sm text-gray-900">
                          {product.currency === 'USD' ? 'US$' : '$'}{product.price}
                          <span className="ml-2 text-xs text-gray-500">{product.currency || 'ARS'}</span>
                        </p>
                        <p className="text-xs text-gray-500">
                          Stock: {product.stock}
                          {product.imageUrls && product.imageUrls.length > 0 && (
                            <span className="ml-2">• {product.imageUrls.length} imágenes</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditProduct(product)}
                        className="bg-yellow-500 text-white px-3 py-1 rounded text-sm hover:bg-yellow-600"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(product.id)}
                        className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600"
                      >
                        Eliminar
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {activeTab === 'categories' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Lista de Categorías
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                Organiza tus productos por categorías
              </p>
            </div>
            {categories.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <p className="text-gray-500">No hay categorías registradas</p>
                <button
                  onClick={() => setShowCategoryForm(true)}
                  className="mt-4 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                >
                  Crear primera categoría
                </button>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {categories.map((category) => (
                  <li key={category.id} className="px-4 py-4 flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">{category.name}</h4>
                          <p className="text-sm text-gray-500">{category.description || 'Sin descripción'}</p>
                        </div>
                        <div className="text-sm text-gray-500">
                          {products.filter(p => p.category?.id === category.id).length} productos
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <button
                        onClick={() => handleEditCategory(category)}
                        className="bg-yellow-500 text-white px-3 py-1 rounded text-sm hover:bg-yellow-600"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(category.id)}
                        className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600"
                      >
                        Eliminar
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {activeTab === 'mercadopago' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Mercado Pago (produccion)
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                Configura las credenciales para el checkout.
              </p>
            </div>
            <div className="px-4 py-6 sm:px-6">
              {isLoadingMercadoPago && (
                <div className="text-sm text-gray-500">Cargando configuracion...</div>
              )}

              {mercadoPagoErrors.length > 0 && (
                <div className="mb-4">
                  {mercadoPagoErrors.map((err, idx) => (
                    <div key={idx} className="text-red-600 text-sm">{err}</div>
                  ))}
                </div>
              )}

              {mercadoPagoSuccess && (
                <div className="mb-4 text-green-600 text-sm">{mercadoPagoSuccess}</div>
              )}

              <form onSubmit={handleSaveMercadoPago} className="space-y-4 max-w-xl">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Access Token</label>
                  <input
                    type="text"
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    value={mercadoPagoConfig.accessToken}
                    onChange={(e) => handleMercadoPagoChange('accessToken', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Public Key</label>
                  <input
                    type="text"
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    value={mercadoPagoConfig.publicKey}
                    onChange={(e) => handleMercadoPagoChange('publicKey', e.target.value)}
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    type="submit"
                    disabled={isSavingMercadoPago}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSavingMercadoPago ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPanel;